/**
 * BlockedUsersPage - Manage Blocked Users
 *
 * Allows users to view and unblock users they've blocked.
 *
 * Issue #920: Add UI for message deletion, user blocking, and reporting
 */

import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TopBar, Avatar, Button, Skeleton, EmptyState } from '../../components';
import { useAuth } from '../../hooks/useAuth';
import { useOroMutation, OroOperation } from '../../hooks/useOroMutation';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { blocksApi, type BlockedUsersResponse } from '../../lib/api/blocks';
import { invalidateBlockSensitiveQueries } from '../../lib/blockSensitiveQueries';
import { Ban } from 'lucide-react';

export default function BlockedUsersPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  useDocumentTitle('Blocked Users');

  // Fetch blocked users
  const { data, isLoading, error } = useQuery<BlockedUsersResponse>({
    queryKey: ['blocked-users'],
    queryFn: blocksApi.getBlockedUsers,
    enabled: !!user,
  });

  // Unblock mutation
  const unblockMutation = useOroMutation<void, string>({
    mutationFn: (userId) => blocksApi.unblockUser(userId),
    oro: { op: OroOperation.UnblockUser },
    successToast: 'User unblocked',
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blocked-users'] });
      // #3258: unblocking must restore the user's content without a hard
      // refresh — cached chat + roster queries were fetched under the block
      // filter and stay fresh for their staleTime. One shared helper
      // invalidates EVERY block-filtered prefix (PR #3266 r2) — same call as
      // the block path on PublicProfilePage, so the lists cannot drift.
      invalidateBlockSensitiveQueries(queryClient);
    },
  });

  const blockedUsers = data?.blocked_users || [];

  if (isLoading) {
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar title="Blocked Users" onBack={() => navigate(-1)} />
        <div className="flex-1 overflow-y-auto pt-14 p-4 space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-16 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar title="Blocked Users" onBack={() => navigate(-1)} />
        <div className="flex-1 overflow-y-auto pt-14 p-4">
          <div className="bg-oro-error/10 border border-oro-error/20 rounded-xl p-4">
            <p className="text-oro-error">Failed to load blocked users. Please try again.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-oro-bg min-h-screen">
      <TopBar title="Blocked Users" onBack={() => navigate(-1)} />

      <div className="flex-1 overflow-y-auto pt-14 p-4">
        {blockedUsers.length === 0 ? (
          <EmptyState
            icon={<Ban className="w-8 h-8 text-text-muted" />}
            title="No blocked users"
            message="You haven't blocked anyone yet."
          />
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-text-secondary mb-4">
              Blocked users can't see your profile or send you messages.
            </p>

            {blockedUsers.map((user) => {
              const displayName = [user.first_name, user.last_name].filter(Boolean).join(' ') || 'Unknown User';

              return (
                <div
                  key={user.id}
                  className="bg-oro-surface border border-oro-border rounded-xl p-4 flex items-center gap-4"
                >
                  <Avatar
                    name={displayName}
                    profile_image_url={user.profile_image_url}
                    size="md"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-text-primary truncate">{displayName}</p>
                    <p className="text-sm text-text-muted">
                      Blocked {new Date(user.blocked_at * 1000).toLocaleDateString()}
                    </p>
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => unblockMutation.mutate(user.id)}
                    disabled={unblockMutation.isPending}
                  >
                    {unblockMutation.isPending ? 'Unblocking...' : 'Unblock'}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
